import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {
	FundOrderTypesEnum,
	IProductDocument,
	ReportExtensions
} from '@spa/api';
import {forkJoin, Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {Helper} from 'src/app/shared/classes';
import {Language} from 'src/app/shared/language';
import {PortalPaths} from 'src/app/shared/paths';
import {environment} from 'src/environments/environment';
import {Product} from '.';
import {MatDialog} from "@angular/material/dialog";
import {CreateOrderDialogComponent} from '../../components/order/create-order-dialog/create-order-dialog.component';

export class ProductHelper {
	private constructor() {

	}

	static createOrderDialog(
		dialog: MatDialog,
		isMobile: boolean,
		symbol: string,
		orderType: FundOrderTypesEnum): void {
		const panelClass = ['order-dialog'];

		if (isMobile) {
			panelClass.push('product-dialog-mobile');
		}

		dialog.open(CreateOrderDialogComponent, {
			panelClass,
			data: {
				orderType: orderType,
				productSymbol: symbol,
			}
		});
	}

	public static isProductAvailable(product: Product, orderType: FundOrderTypesEnum): boolean {
		let isAvailable = !!product;

		if (isAvailable && orderType === FundOrderTypesEnum.Sell && !product.sellOrdersAllowed) {
			isAvailable = false;
		} else if (isAvailable && orderType === FundOrderTypesEnum.Buy && !product.buyOrdersAllowed) {
			isAvailable = false;
		}

		return isAvailable;
	}


	static getProductMaturityFeeValueText(product: Product, currentLanguage: string): string {
		const min = product.withdrawalBeforeMaturityFeeMin;
		const max = product.withdrawalBeforeMaturityFeeMax;

		if (!min && !max) {
			return '';
		}

		const isEnglish = currentLanguage === Language.English;
		const toDecimal = (amount: number) => `${ReportExtensions.toDecimalString(amount, 2, 2, false)}%`;

		const maxDecimal = toDecimal(max);
		const minDecimal = toDecimal(min);

		if (min && !max) {
			return isEnglish
				? `from ${minDecimal}`
				: `от ${minDecimal}`;
		}

		if (!min && max) {
			return isEnglish
				? `up to ${maxDecimal}`
				: `до ${maxDecimal}`;
		}

		return isEnglish
			? `from ${minDecimal} to ${maxDecimal}`
			: `от ${minDecimal} до ${maxDecimal}`;
	}


	static getProductYieldValueText(product: Product, currentLanguage: string): string {
		return '';
	}


	static getProductYieldValueHtml(product: Product, currentLanguage: string): string {
		return '';
	}

	static getProductYieldLabelText(product: Product, currentLanguage: string): string {
		return '';
	}

	static getDurationInMonthsValue(product: Product): string {
		if (product.durationInMonths % 12 === 0) {
			return ReportExtensions.toIntegerString(product.durationInMonths / 12, false);
		} else {
			return ReportExtensions.toIntegerString(product.durationInMonths, false);
		}
	}

	static getDurationInMonthsString(product: Product, currentLanguage: string): string {
		const isEnglish = currentLanguage === Language.English;

		if (product.durationInMonths % 12 === 0) {
			const isSingleYear = product.durationInMonths === 12;

			if (isEnglish) {
				return isSingleYear ? 'year' : 'years';
			}

			return isSingleYear ? 'година' : 'години';
		}

		const isSingleMonth = product.durationInMonths === 1;

		if (isEnglish) {
			return isSingleMonth ? 'month' : 'months';
		}

		return isSingleMonth ? 'месец' : 'месеца';
	}

	static getMaturityLabel(product: Product, currentLanguage: string): string {
		const isEnglish = currentLanguage === Language.English;

		return isEnglish ? 'tenor' : 'срок на инвестицията';
	}

	static downloadProductDocuments(
		httpClient: HttpClient,
		product: Product,
		currentLanguage: string): Subscription {
		const documents = product.productDocuments
			.filter(document => document.language === currentLanguage);

		return ProductHelper.downloadDocuments(httpClient, documents);
	}

	static downloadDocuments(
		httpClient: HttpClient,
		documents: IProductDocument[]): Subscription {
		const requests: Observable<void>[] = [];

		if (documents && documents.length) {
			documents.forEach(document => {
				const url = `${environment.cdnUrl}${document.fileStorageId}`;
				const request = httpClient.get(url, {responseType: 'blob'})
					.pipe(map(blob => {
						Helper.filesHelper().triggerDownloadFile(document.name, blob);
					}));

				requests.push(request);
			});
		}

		return forkJoin(requests).subscribe();
	}

	static paginateCollection(collection: Product[], start = 0, end = 10): Product[] {
		return collection.filter((item, index) => {
			return index >= start && index < end;
		});
	}

	static search(router: Router, language: string, keyword: string, full: boolean): void {
		const queryParams = {
			keyword,
			full: '1'
		};

		const commands = [[
			language,
			PortalPaths.root,
			PortalPaths.dashboard.path,
		].join('/')];

		router.navigate(commands, {
			queryParams
		}).finally();
	}
}
